import type { FC, InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../../utils/cn';
import uncheckedIcon from '../../../assets/svgs/unchecked-icon.svg';
import checkedIcon from '../../../assets/svgs/checked-icon.svg';

type CheckboxProps = {
    name: string;
    label?: ReactNode;
    checked: boolean;
    onChange: (checked: boolean) => void;
    error?: string;
    touched?: boolean;
    className?: string;
    showError?: boolean;
    inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'checked'>;
};

const Checkbox: FC<CheckboxProps> = ({
    name,
    label,
    checked,
    onChange,
    error,
    touched = false,
    className,
    showError = true,
    inputProps,
}) => {
    const hasError = Boolean(touched && error);
    const errorId = `${name}-error`;

    return (
        <div className={cn('w-full', className)}>
            <label className="flex items-start text-[11px] text-gray-800 cursor-pointer">
                <input
                    {...inputProps}
                    id={name}
                    name={name}
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                        onChange(event.target.checked);
                        inputProps?.onChange?.(event);
                    }}
                    aria-invalid={hasError}
                    aria-describedby={hasError ? errorId : undefined}
                    className={cn('w-0 h-0 opacity-0 peer', inputProps?.className)}
                />

                <span
                    className="flex items-center justify-center peer-focus-visible:ring-2 peer-focus-visible:ring-primary rounded-sm"
                    aria-hidden="true"
                >
                    <img
                        src={checked ? checkedIcon : uncheckedIcon}
                        alt=""
                        width={18}
                        height={18}
                        className="min-w-[1.125rem]"
                    />
                </span>

                {label && <span className="text-[11px] !leading-relaxed ml-2">{label}</span>}
            </label>

            {showError && hasError && (
                <p id={errorId} className="mt-1 text-[11px] text-error-primary">
                    {error}
                </p>
            )}
        </div>
    );
};

export default Checkbox;
